import type { FxConversion } from '@/constants/FxConversion';
import type { IKdeRegressionsDto } from '@/types/IKdeRegressionsDto';

export enum BaseCalcResponseKey {
  BETA = 'beta',
  STANDARD_ERROR = 'betaSE',
  T_STAT = 'tStat',
  P_VALUE = 'pValue',
  LOWER_BOUND = 'lowerBound',
  UPPER_BOUND = 'upperBound',
  VARIANCE_ATTRIBUTION = 'varAttr',
  VARIANCE_PROPORTION = 'varProp',
  RETURN_PA_ATTRIBUTION = 'returnAttr',
  RETURN_PA_PROPORTION = 'returnProp',
  FACTOR_STANDARD_ERROR = 'factorStandardError',
  FACTOR_VOL = 'factorVol',
  FACTOR_CORRELATION = 'factorCorr',
}

export enum RegressionSummaryCalcResponseKey {
  R_SQUARED = 'rSquared',
}

export enum BaseCalcResponseKeyPrettyTranslation {
  BETA = 'Beta Coefficient',
  STANDARD_ERROR = 'Standard Error',
  T_STAT = 'T-stat',
  P_VALUE = 'P-value',
  LOWER_BOUND = 'Lower 95%',
  UPPER_BOUND = 'Upper 95%',
  VARIANCE_ATTRIBUTION = 'Variance Attribution',
  VARIANCE_PROPORTION = 'Variance Proportion',
  RETURN_PA_ATTRIBUTION = 'Return p.a. Attribution',
  RETURN_PA_PROPORTION = 'Return p.a. Proportion',
  FACTOR_STANDARD_ERROR = 'Standard Error Factor Fit',
  FACTOR_VOL = 'Factor Volatility',
  FACTOR_CORRELATION = 'Factor Correlation',
}

export interface BaseCalcResponse {
  /**
   * Standard Error
   * available in dropdown menu
   */
  [BaseCalcResponseKey.STANDARD_ERROR]: number;

  /**
   * Alpha and beta t-statistics of regression over the time period
   * available in dropdown menu
   */
  [BaseCalcResponseKey.T_STAT]: number;

  /**
   * Alpha and beta p-values of regression over the time period
   * available in dropdown menu
   */
  [BaseCalcResponseKey.P_VALUE]: number;

  /**
   * Alpha and beta 95% confidence interval lower bounds
   * of regression over the time period.
   * Alpha lower bound is annualised.
   * available in dropdown menu
   */
  [BaseCalcResponseKey.LOWER_BOUND]: number;

  /**
   * Alpha and beta 95% confidence interval upper bounds
   * of regression over the time period.
   * Alpha upper bound is annualised.
   * available in dropdown menu
   */
  [BaseCalcResponseKey.UPPER_BOUND]: number;

  /**
   * Variance attribution
   * available in dropdown menu
   */
  [BaseCalcResponseKey.VARIANCE_ATTRIBUTION]: number;

  /**
   * Variance proportion
   * available in dropdown menu
   */
  [BaseCalcResponseKey.VARIANCE_PROPORTION]: number;

  /**
   * Return p.a. Attribution
   * available in dropdown menu
   */
  [BaseCalcResponseKey.RETURN_PA_ATTRIBUTION]: number;

  /**
   * Factor Standard Error
   * available in dropdown menu
   */
  [BaseCalcResponseKey.FACTOR_STANDARD_ERROR]: number;

  /**
   * Factor Correlation
   * available in dropdown menu
   */
  [BaseCalcResponseKey.FACTOR_CORRELATION]: number;

  /**
   * Factor Volatility
   * available in dropdown menu
   */
  [BaseCalcResponseKey.FACTOR_VOL]: number;

  /**
   * Return p.a. Proportion
   * available in dropdown menu
   */
  [BaseCalcResponseKey.RETURN_PA_PROPORTION]: number;
}

interface ConvexityData {
  /**
   * Coefficients of 2nd and 3rd degree polynomial regressions
   * of residuals on regression track return
   *
   * For this array of arrays and others in the IRegressionCalcResponse,
   * each array corresponds to a regressor. The order of the regressors
   * corresponds to the order of the regressors returned in the regressors
   * array.
   */
  polyCoeff: number[][];

  /**
   * R Squared of 2nd and 3rd degree polynomial regressions
   * of residuals on regression track return
   */
  polyRSquared: number[];

  /**
   * R Squared of 2nd and 3rd degree polynomial regressions
   * of residuals on portfolio track return
   */
  polyRSquaredPft: number[];
}

interface CalcResponse extends BaseCalcResponse, ConvexityData {
  startDate: string;
  endDate: string;
  trainEndDate: string;
  noElements: number;
  beta: number[];

  rSquared: number;
  rSquaredAdj: number;

  /**
   * F statistic of regression over the time period
   */
  fStat?: number;

  /**
   * F-test p-value of regression over the time period
   */
  fProb?: number;

  /**
   * Annualised returns attributed to alpha and beta of regression over the time period.
   */
  returnAttribution: number[];

  /**
   * Histogram of residuals.
   * First number is mid-point of the bucket;
   * second number is the density value
   *
   * For this array of arrays and others in the IRegressionCalcResponse,
   * each array corresponds to a regressor. The order of the regressors
   * corresponds to the order of the regressors returned in the regressors
   * array.
   */
  histogram: number[][];

  /**
   * Density plot of residuals.
   * First number is the residual value;
   * second number is the KDE value;
   * third number is the normal density value
   */
  density: number[][];

  /**
   * x,y coordinates of QQ plot
   */
  qqPlot: number[][];
  rtnQQPlot: number[][];
  regressionQQPlot: number[][];

  residSkew: number;
  residKurtosis: number;

  /**
   * Mean squared error
   */
  mse: number;

  /**
   * Mean absolute error
   */
  mae: number;

  /**
   * Mean absolute percent error
   */
  mape: number;

  /**
   * Out of sample mean squared error
   */
  mapeOut: number;

  /**
   * Out of sample mean error
   */
  meOut: number;

  /**
   * Out of sample R squared
   */
  rSquaredOut: number;

  /**
   * Out of sample alpha
   */
  alphaOut: number;

  /**
   * Out of sample Annualised variance attributed to alpha
   * and beta of regression over the time period.
   */
  varAttributionOut: number[];

  /**
   * Out of sample proportions of variance attributed to alpha
   * and beta of regression over the time period.
   */
  varPropOut: number[];

  /**
   * Out of sample annualised returns attributed to alpha
   * and beta of regression over the time period.
   */
  returnAttributionOut: number[];

  /**
   * Out of sample proportions of returns attributed to alpha
   * and beta of regression over the time period.
   */
  returnPropOut: number[];

  timeseriesDates: string[];

  /**
   * Dates used for monthly return and 12-month rolling return
   */
  monthlyTimeseriesDates: string[];

  /**
   * Rolling 12-month return
   */
  monthlyRollingObservedReturns: number[];

  /**
   * Monthly rolling return of observed track + 1SD
   * Upper Bollinger Band
   */
  monthlyRollingReplicatedReturnsAveragePlusSdv: number[];

  /**
   * Monthly rolling return of observed track - 1SD
   * Lower Bollinger Band
   */
  monthlyRollingReplicatedReturnsAverageMinusSdv: number[];

  /**
   * Monthly return of observed track
   */
  monthlyObservedReturns: number[];

  /**
   * Monthly return of observed track + 1SD
   * Upper Bollinger Band
   */
  monthlyObservedReturnsAveragePlusSdv: number[];

  /**
   * Monthly return of observed track - 1SD
   * Lower Bollinger Band
   */
  monthlyObservedReturnsAverageMinusSdv: number[];

  /**
   * Rolling 12-month return of replicated track
   */
  monthlyRollingReplicatedReturns: number[];

  /**
   * Monthly return of replicated track
   */
  monthlyReplicatedReturns: number[];

  /**
   * Monthly return of replicated track + 1SD
   * Upper Bollinger Band
   */
  monthlyReplicatedReturnsAveragePlusSdv: number[];

  /**
   * Monthly return of replicated track - 1SD
   * Lower Bollinger Band
   */
  monthlyReplicatedReturnsAverageMinusSdv: number[];

  /**
   * Factor Adjusted Monthly Rolling return of observed track
   */
  monthlyRollingDiffReturns: number[];

  /**
   * Factor Adjusted Monthly return of observed track
   */
  monthlyDiffReturns: number[];

  /**
   * Factor Adjusted Monthly return of observed track + 1SD
   * Upper Bollinger Band
   */
  monthlyDiffReturnsAveragePlusSdv: number[];

  /**
   * Factor Adjusted Monthly return of observed track - 1SD
   * Lower Bollinger Band
   */
  monthlyDiffReturnsAverageMinusSdv: number[];

  /**
   * Difference between the observed track and the replicated track divided by 100
   */
  timeseriesCumDiff: number[];

  /**
   * Used for exporting the residual track
   */
  dailyTimeseriesDates: string[];

  /**
   * Used for exporting the residual track
   */
  dailyTimeseriesCumDiff: number[];

  /**
   * Alpha and beta of rolling regression. Alpha is annualised.
   */
  rollingTimeseriesDates: string[];

  /**
   * Difference between the rolling observed track and the rolling replicated track divided by 100
   */
  rollingTimeseriesCumDiff: number[];

  /**
   * Difference between the rolling observed track and the rolling replicated track divided by 100
   * Used for exporting the rolling residual track
   * TODO: uncomment when added to the API
   */
  // dailyRollingTimeseriesCumDiff: number[];

  /**
   * Alpha and beta of rolling regression. Alpha is annualised.
   * Used for exporting the rolling residual track
   * TODO: uncomment when added to the API
   */
  // dailyRollingTimeseriesDates: string[];

  /**
   * Replicated regression track
   */
  timeseriesReplicated: number[];

  /**
   * Observed regression track
   */
  timeseriesObserved: number[];

  /**
   * Out of sample replicated regression track
   */
  outOfSampleReplicated: number[];

  /**
   * Index of active premia under Lasso
   */
  activePremia: number[];

  /**
   * Dates of rolling regression
   */
  rollingDates: string[];

  /**
   * This is the label for a given rolling period that the tooltip
   * should be displaying
   */
  rollingTooltipStartDate: string[];

  /**
   * The date at which to trigger the tooltip for the given bar
   * on the rolling regression
   */
  rollingMidDateList: string[];

  /**
   * The start date of the bars in the rolling regression
   */
  rollingStartDateList: string[];

  /**
   * The end date for the bar on the rolling regression
   */
  rollingEndDateList: string[];

  /**
   * Alpha and beta of rolling regression. Alpha is annualised.
   * For this array of arrays and others in the IRegressionCalcResponse,
   * each array corresponds to a regressor. The order of the regressors
   * corresponds to the order of the regressors returned in the regressors
   * array.
   */
  rollingBeta: number[][];

  /**
   * Alpha and beta standard errors of rolling regression. Alpha standard error is annualised.
   * For this array of arrays and others in the IRegressionCalcResponse,
   * each array corresponds to a regressor. The order of the regressors
   * corresponds to the order of the regressors returned in the regressors
   * array.
   */
  rollingBetaSE: number[][];

  /**
   * Alpha and beta t-statistics of rolling regression
   * For this array of arrays and others in the IRegressionCalcResponse,
   * each array corresponds to a regressor. The order of the regressors
   * corresponds to the order of the regressors returned in the regressors
   * array.
   */
  rollingTStat: number[][];

  /**
   * Alpha and beta p-values of rolling regression
   * For this array of arrays and others in the IRegressionCalcResponse,
   * each array corresponds to a regressor. The order of the regressors
   * corresponds to the order of the regressors returned in the regressors
   * array.
   */
  rollingPValue: number[][];

  /**
   * Alpha and beta 95% confidence interval lower bounds
   * of rolling regression. Alpha lower bound is annualised.
   * For this array of arrays and others in the IRegressionCalcResponse,
   * each array corresponds to a regressor. The order of the regressors
   * corresponds to the order of the regressors returned in the regressors
   * array.
   */
  rollingLowerBound: number[][];

  /**
   * Alpha and beta 95% confidence interval upper bounds
   * of rolling regression. Alpha upper bound is annualised.
   * For this array of arrays and others in the IRegressionCalcResponse,
   * each array corresponds to a regressor. The order of the regressors
   * corresponds to the order of the regressors returned in the regressors
   * array.
   */
  rollingUpperBound: number[][];

  /**
   * R squared of rolling regression
   */
  rollingRSquared: number[];

  /**
   * Adjusted R squared of rolling regression
   */
  rollingRSquaredAdj: number[];

  /**
   * F statistic of rolling regression
   */
  rollingFStat: number[];

  /**
   * F-test p-value of rolling regression
   */
  rollingFProb: number[];

  /**
   * Annualised variance attributed to alpha and beta of rolling regression
   */
  rollingVarAttribution: number[];

  /**
   * Proportions of variance attributed to alpha and beta of rolling regression
   * For this array of arrays and others in the IRegressionCalcResponse,
   * each array corresponds to a regressor. The order of the regressors
   * corresponds to the order of the regressors returned in the regressors
   * array.
   */
  rollingVarProp: number[][];

  /**
   * Annualised returns attributed to alpha and beta of rolling regression
   * For this array of arrays and others in the IRegressionCalcResponse,
   * each array corresponds to a regressor. The order of the regressors
   * corresponds to the order of the regressors returned in the regressors
   * array.
   */
  rollingReturnAttribution: number[][];

  /**
   * Proportions of returns attributed to alpha and beta of rolling regression
   * For this array of arrays and others in the IRegressionCalcResponse,
   * each array corresponds to a regressor. The order of the regressors
   * corresponds to the order of the regressors returned in the regressors
   * array.
   */
  rollingReturnProp: number[][];

  /**
   * Rolling replicated track
   */
  rollingTimeseriesReplicated: number[];

  /**
   * Rolling observed track
   */
  rollingTimeseriesObserved: number[];

  /**
   * Metrics of Regression track
   */
  monthlyReturnPA: number;

  monthlyVolatility: number;

  monthlySkew: number;

  monthlyKurtosis: number;

  quarterlyRollingReplicatedReturns: number[];

  quarterlyRollingObservedReturns: number[];

  quarterlyRollingReplicatedReturnsAveragePlusSdv: number[];

  quarterlyRollingReplicatedReturnsAverageMinusSdv: number[];

  monthlyReplicatedReturnsRollingAverage: number[];

  monthlyRollingReplicatedReturnsRollingAverage: number[];

  quarterlyRollingReplicatedReturnsRollingAverage: number[];
}

export interface IRegressionCalcResponseRegressor extends BaseCalcResponse {
  code: string;
  beta: number;
  pValue: number;
  returnAttr: number;
  beta_track: number[];
  returnAttr_track: number[];
  varAttrOut: number | null;
  varProp_track: number[];
  cumulated_dates: string[];
  cumulated_track: number[];
  observed_track: number[];
  /**
   * If there are less than 3 data points between `customEndDate` and
   * `endDate`, then out_sample_track cannot be generated
   */
  out_sample_track: number[];
  replicated_track: number[];
  fxType?: FxConversion;

  factorStandardError: number;
  factorVol: number;
  factorCorr: number;
  regimeMetrics?: {
    [regimeName: string]: {
      beta: number;
      pValue: number;
      /**
       * we use `returnAttr` to match the form of the response we get for the original /result
       * API for the `regressors`
       */
      returnAttr: number;
      varProp: number;
    };
  };
}

export interface IRegressionCalcResponseRegimeRegressor extends IRegressionCalcResponseRegressor {
  regimeMetrics?: {
    [regimeName: string]: {
      beta: number;
      pValue: number;
      /**
       * we use `returnAttr` to match the form of the response we get for the original /result
       * API for the `regressors`
       */
      returnAttr: number;
      varProp: number;
    };
  };
}

export interface BaseRegressionCalcResponse {
  name?: string;
  calculatedResponse: CalcResponse;
  regressors: IRegressionCalcResponseRegressor[];
  invalidRegressors: string[];
}

export interface IRegressionCalcResponse extends BaseRegressionCalcResponse {
  regimeMetrics?: {
    name: string;
    /**
     * Number of items in the array matches the number of regressors
     */
    beta: number[];

    /**
     * Number of items in the array matches the number of regressors
     */
    pValue: number[];

    /**
     * Number of items in the array matches the number of regressors
     */
    returnAttribution: number[];

    /**
     * Number of items in the array matches the number of regressors
     */
    varProp: number[];

    polyCoeff: number[][];
    polyRSquared: number[];
    polyRSquaredPft: number[];
    regressors: IRegressionCalcResponseRegressor[];
  }[];
}

export interface IKdeRegressionCalcResponse extends BaseRegressionCalcResponse {
  kdeRegressions: IKdeRegressionsDto;
  regimeMetrics?: {
    name: string;
    kdeRegressions: IKdeRegressionsDto;
    monthlyReturnPA: number;
    monthlyVolatility: number;
    monthlySkew: number;
    monthlyKurtosis: number;
  }[];
}

export interface IQQPlotRegressionCalcResponse extends BaseRegressionCalcResponse {
  kdeRegressions: IKdeRegressionsDto;
  regimeMetrics?: {
    name: string;
    qqPlot: number[][];
    rtnQQPlot: number[][];
    regressionQQPlot: number[][];
  }[];
}
